import { useEffect, useState, type FormEvent } from 'react';
import {
  annonceOuverte,
  normaliserTelephone,
  telephoneLisible,
  type AnnonceResume,
  type CreationCandidatureCv,
  type DepotCvDetail,
} from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

interface Props {
  depot: DepotCvDetail;
  envoi: boolean;
  onAnnuler: () => void;
  /** Saisie complète et confirmée : la carte envoie la demande et rend compte. */
  onValider: (corps: CreationCandidatureCv, destination: string) => void;
}

/**
 * « Affecter à une annonce » (docs/UI.md § CV à rattacher) : le CV est celui
 * d'un candidat qu'aucune fiche ne connaît. Le responsable RH recopie
 * l'identité lue dans le CV et choisit une annonce **ouverte** — ni brouillon,
 * ni close, ni archivée, puisqu'on ne postule qu'à celles-là. La fiche et la
 * candidature naissent côté serveur, et le CV s'y rattache.
 *
 * Les coordonnées lues dans le CV sont proposées d'un clic : elles servent de
 * clé anti-doublon, et rien ne part sans que l'utilisateur les ait vues.
 */
export function CreerCandidatureCv({ depot, envoi, onAnnuler, onValider }: Props) {
  const [annonces, setAnnonces] = useState<AnnonceResume[] | null>(null);
  const [annonceId, setAnnonceId] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState(depot.coordonnees.telephones[0] ?? '');
  const [email, setEmail] = useState(depot.coordonnees.emails[0] ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => vivant && setAnnonces(liste.filter((annonce) => annonceOuverte(annonce.statut))))
      .catch(() => vivant && setAnnonces([]));
    return () => {
      vivant = false;
    };
  }, []);

  function creer(evenement: FormEvent): void {
    evenement.preventDefault();
    setErreur(null);

    const annonce = annonces?.find((a) => a.id === annonceId);
    if (!annonce) {
      setErreur('Choisir l’annonce à laquelle affecter cette candidature');
      return;
    }
    if (prenom.trim().length < 2 || nom.trim().length < 2) {
      setErreur('Donner le prénom et le nom du candidat (2 caractères au moins)');
      return;
    }
    // Le serveur n'accepte que le format +216XXXXXXXX : la saisie « 98 765 432 » se normalise ici.
    const numero = telephone.trim() ? normaliserTelephone(telephone) : null;
    if (telephone.trim() && !numero) {
      setErreur('Téléphone incompréhensible : 8 chiffres, ou +216 suivi de 8 chiffres');
      return;
    }
    const adresse = email.trim().toLowerCase();
    if (!numero && !adresse) {
      setErreur('Donner au moins le téléphone ou l’e-mail du candidat');
      return;
    }

    const destination = `${prenom.trim()} ${nom.trim()} — ${annonce.intitule}`;
    if (!window.confirm(`Créer la candidature de ${destination} et y rattacher ce CV ?`)) return;

    onValider(
      {
        annonceId,
        prenom: prenom.trim(),
        nom: nom.trim(),
        ...(numero ? { telephone: numero } : {}),
        ...(adresse ? { email: adresse } : {}),
      },
      destination,
    );
  }

  return (
    <div className="panneau-cv">
      <h4>Affecter à une annonce</h4>
      <p className="mention">
        Pour un candidat qu’aucune fiche ne connaît : sa fiche et sa candidature sont créées ici, puis le CV y est
        rattaché. Si le téléphone ou l’e-mail saisi est déjà celui d’une fiche, c’est cette fiche qui sert — aucun
        doublon n’est créé.
      </p>

      <form className="formulaire-candidature-cv" onSubmit={creer}>
        <label className="champ">
          <span>Prénom</span>
          <input type="text" value={prenom} maxLength={80} autoComplete="off" onChange={(e) => setPrenom(e.target.value)} />
        </label>
        <label className="champ">
          <span>Nom</span>
          <input type="text" value={nom} maxLength={80} autoComplete="off" onChange={(e) => setNom(e.target.value)} />
        </label>

        <label className="champ">
          <span>Téléphone</span>
          <input
            type="tel"
            inputMode="tel"
            value={telephone}
            maxLength={20}
            autoComplete="off"
            onChange={(e) => setTelephone(e.target.value)}
          />
        </label>
        <Proposees
          libelle="Lus dans le CV"
          valeurs={depot.coordonnees.telephones}
          afficher={telephoneLisible}
          onChoisir={setTelephone}
        />

        <label className="champ">
          <span>E-mail</span>
          <input
            type="email"
            inputMode="email"
            value={email}
            maxLength={254}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <Proposees
          libelle="Lus dans le CV"
          valeurs={depot.coordonnees.emails}
          afficher={(valeur) => valeur}
          onChoisir={setEmail}
        />

        <label className="champ">
          <span>Annonce</span>
          <select value={annonceId} disabled={!annonces} onChange={(e) => setAnnonceId(e.target.value)}>
            <option value="">choisir une annonce…</option>
            {(annonces ?? []).map((annonce) => (
              <option key={annonce.id} value={annonce.id}>
                {annonce.reference ? `${annonce.reference} — ` : ''}
                {annonce.intitule}
              </option>
            ))}
          </select>
        </label>
        {annonces?.length === 0 && (
          <p className="mention">Aucune annonce validée ni publiée : validez d’abord une annonce.</p>
        )}

        {erreur && <Erreur message={erreur} />}
        <div className="actions">
          <button type="submit" className="bouton" disabled={envoi || !annonces || annonces.length === 0}>
            Créer la candidature
          </button>
          <button type="button" className="lien" onClick={onAnnuler}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

/** Coordonnées lues dans le CV : un clic les recopie dans le champ. */
function Proposees({
  libelle,
  valeurs,
  afficher,
  onChoisir,
}: {
  libelle: string;
  valeurs: readonly string[];
  afficher: (valeur: string) => string;
  onChoisir: (valeur: string) => void;
}): JSX.Element | null {
  if (valeurs.length === 0) return null;
  return (
    <p className="coordonnees-proposees">
      <span className="mention">{libelle} :</span>
      {valeurs.map((valeur) => (
        <button key={valeur} type="button" className="lien" onClick={() => onChoisir(valeur)}>
          {afficher(valeur)}
        </button>
      ))}
    </p>
  );
}
